import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMinimize2, FiInfo } from 'react-icons/fi';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import ProcessingLoader from '../components/ProcessingLoader';
import ResultDisplay from '../components/ResultDisplay';
import './ToolPage.css';

const ImageCompression = () => {
  const [file, setFile] = useState(null);
  const [originalPreview, setOriginalPreview] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [quality, setQuality] = useState(80);
  const [compressionResult, setCompressionResult] = useState(null);

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setOriginalPreview(selectedFile ? URL.createObjectURL(selectedFile) : null);
    setProcessedImage(null);
    setError(null);
    setCompressionResult(null);
  };

  const handleProcess = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('quality', quality);

    try {
      const response = await axios.post('/api/compress', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });

      const imageUrl = URL.createObjectURL(response.data);
      setProcessedImage(imageUrl);
      
      const originalSize = file.size;
      const compressedSize = response.data.size;
      const savings = Math.round((1 - compressedSize / originalSize) * 100);
      
      setCompressionResult({
        originalSize: (originalSize / 1024).toFixed(1),
        compressedSize: (compressedSize / 1024).toFixed(1),
        savings
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to compress image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!processedImage) return;
    
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = `compressed-${Date.now()}.jpg`;
    link.click();
  };

  const handleReset = () => {
    setFile(null);
    setOriginalPreview(null);
    setProcessedImage(null);
    setError(null);
    setCompressionResult(null);
  };

  return (
    <div className="tool-page">
      {isProcessing && (
        <ProcessingLoader 
          message="Compressing image..."
          subMessage="Optimizing file size while preserving quality"
        />
      )}

      <div className="tool-hero">
        <div className="container">
          <motion.div 
            className="tool-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="tool-icon gradient-success">
              <FiMinimize2 />
            </div>
            <h1>Image Compression</h1>
            <p>Reduce file sizes without losing quality. Optimize your images for web, 
               email, or storage.</p>
          </motion.div>
        </div>
      </div>

      <div className="tool-content">
        <div className="container">
          {!processedImage ? (
            <motion.div 
              className="tool-upload-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ImageUploader 
                onFileSelect={handleFileSelect}
                title="Upload image to compress"
                subtitle="Reduce file size while keeping quality"
              />

              {file && (
                <motion.div 
                  className="options-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="option-group">
                    <label className="option-label">
                      Quality: {quality}%
                    </label>
                    <input 
                      type="range" 
                      min="10" 
                      max="100" 
                      value={quality}
                      onChange={(e) => setQuality(Number(e.target.value))}
                      className="quality-slider"
                    />
                    <div className="slider-labels">
                      <span>Smaller file</span>
                      <span>Higher quality</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {file && (
                <motion.div 
                  className="process-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <button 
                    className="btn-process"
                    onClick={handleProcess}
                    disabled={isProcessing}
                  >
                    Compress Image
                  </button>
                </motion.div>
              )}

              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <>
              {compressionResult && (
                <motion.div 
                  className="compression-stats"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="stat-box">
                    <span className="stat-label">Original</span>
                    <span className="stat-value">{compressionResult.originalSize} KB</span>
                  </div>
                  <div className="stat-arrow">→</div>
                  <div className="stat-box">
                    <span className="stat-label">Compressed</span>
                    <span className="stat-value">{compressionResult.compressedSize} KB</span>
                  </div>
                  <div className="stat-box savings">
                    <span className="stat-label">Savings</span>
                    <span className="stat-value">{compressionResult.savings}%</span>
                  </div>
                </motion.div>
              )}
              <ResultDisplay
                originalImage={originalPreview}
                processedImage={processedImage}
                onDownload={handleDownload}
                onReset={handleReset}
              />
            </>
          )}

          <div className="tool-info-section">
            <div className="info-card">
              <FiInfo className="info-icon" />
              <h3>How it works</h3>
              <p>Our compression algorithm intelligently reduces file size by removing unnecessary 
                 data while preserving visual quality. Adjust the quality slider to find the 
                 perfect balance between size and quality.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCompression;
